import jsPDF from "jspdf";
import templateImage from "@/assets/vitality-bilan-template.png";
import {
  SCORING_CATEGORIES,
  computeCategoryScore,
  computeGlobalScore,
  interpretGlobalScore,
} from "@/data/vitalityScoringData";

interface PatientLike {
  name?: string;
  date_of_birth?: string | null;
  gender?: string | null;
}

// Interpret a score /100 (used for gauge color only).
function interpretScore100(score: number): { label: string; color: [number, number, number] } {
  if (score < 50) return { label: "FRAGILE", color: [218, 138, 130] };
  if (score < 70) return { label: "À AMÉLIORER", color: [212, 165, 116] };
  return { label: "BON", color: [122, 153, 104] };
}

// Interpret a pillar raw score /20 with the brand's 4-tier scale.
function interpretPillarRaw(raw: number): { label: string; color: [number, number, number] } {
  if (raw < 5) return { label: "FRAGILE", color: [218, 138, 130] };
  if (raw < 10) return { label: "À AMÉLIORER", color: [212, 165, 116] };
  if (raw < 15) return { label: "CORRECT", color: [191, 167, 92] };
  return { label: "BON", color: [122, 153, 104] };
}

// Convert "#rrggbb" → [r,g,b]
function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace("#", "");
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}

// Layout positions in mm on A4 (210x297). Calibrated to the new template image.
const PILLAR_POSITIONS = [
  // [centerXmm, baseYmm]
  { x: 36, y: 169 }, // SOMMEIL
  { x: 105, y: 169 }, // DIGESTION
  { x: 174, y: 169 }, // STRESS & MENTAL
  { x: 36, y: 207 }, // ALIMENTATION
  { x: 105, y: 207 }, // MOUVEMENT
  { x: 174, y: 207 }, // ENERGIE
];
// Order must match SCORING_CATEGORIES order
const PILLAR_ORDER = ["sommeil", "digestion", "stress", "alimentation", "mouvement", "energie"];

export async function generateVitalityScoringPDF(
  assessment: any,
  patientOrName: PatientLike | string,
  bilanText?: string,
) {
  const patient: PatientLike =
    typeof patientOrName === "string" ? { name: patientOrName } : patientOrName || {};
  const patientName = patient.name || "—";

  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageWidth = 210;
  const pageHeight = 297;

  const responses: Record<string, number> = assessment.responses || {};

  // Scores converted to /100
  const globalRaw = computeGlobalScore(responses); // /120
  const globalScore = Math.round((globalRaw / 120) * 100);
  const globalInterp = interpretScore100(globalScore);

  // === Background template ===
  doc.addImage(templateImage, "PNG", 0, 0, pageWidth, pageHeight);

  // === GAUGE: needle arrow ===
  // Base shifted +20px X (+5.29mm) from prior (+6.61) → total +11.90mm; Y kept (+3.97mm)
  const gaugeCx = 34 + 6.61 + 5.29 + 1.32;
  const gaugeCy = 100 + 3.97;
  const gaugeR = 22;
  // Angle: score 0 → 180° (left/red), score 100 → 0° (right/green)
  const angleDeg = 180 - (globalScore / 100) * 180;
  const angleRad = (angleDeg * Math.PI) / 180;
  const needleLen = gaugeR - 4;
  const nx = gaugeCx + needleLen * Math.cos(angleRad);
  const ny = gaugeCy - needleLen * Math.sin(angleRad);
  doc.setDrawColor(60, 60, 60);
  doc.setLineWidth(0.9);
  doc.line(gaugeCx, gaugeCy, nx, ny);
  // Pivot
  doc.setFillColor(60, 60, 60);
  doc.circle(gaugeCx, gaugeCy, 1.6, "F");

  // === GLOBAL SCORE NUMBER ===
  // From prior anchor: +20px X (+5.29mm) right, -30px Y (-7.94mm) up
  const globalScoreX = 34 + 5.29 + 5.29 + 2.65;
  const globalScoreY = 117 + 3.97 - 7.94;
  doc.setFont("times", "bold");
  doc.setFontSize(28);
  doc.setTextColor(56, 76, 47);
  doc.text(`${globalRaw}`, globalScoreX, globalScoreY, { align: "center" });

  // === GLOBAL MENTION (label from interpretGlobalScore on /120) ===
  const globalMention = interpretGlobalScore(globalRaw);
  const mentionRgb = hexToRgb(globalMention.color);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  
  const mentionLabel = globalMention.label;
  const mentionTextW = doc.getTextWidth(mentionLabel);
  const mentionPadX = 4;
  const mentionBoxH = 7;
  const mentionBoxW = mentionTextW + mentionPadX * 2;
  const mentionCenterY = globalScoreY + 7 + 7.94 + 1.32; // visual center of badge
  const mentionBoxX = globalScoreX - mentionBoxW / 2;
  const mentionBoxY = mentionCenterY - mentionBoxH / 2;
  doc.setFillColor(...mentionRgb);
  doc.roundedRect(mentionBoxX, mentionBoxY, mentionBoxW, mentionBoxH, 3, 3, "F");
  doc.setTextColor(255, 255, 255);
  // Baseline offset to vertically center the cap height inside the badge
  doc.text(mentionLabel, globalScoreX, mentionCenterY + 12 * 0.35 / 2 - 0.79, { align: "center" });

  // === PILLAR SCORES ===
  // Per-pillar fine-tuning offsets in mm (1px ≈ 0.2646mm)

  const PILLAR_SCORE_OFFSETS: Array<{ x: number; y: number }> = [
    { x: 0,     y: -0.53 }, // SOMMEIL: +2px X, +2px Y
    { x: 0.53,  y: -0.53 }, // DIGESTION: +2px X, +1px Y
    { x: 0.27,  y: -0.53 }, // STRESS: +1px Y
    { x: 1.32,  y: 0.26 },  // ALIMENTATION: +3px X
    { x: 0,     y: 0.26 },  // MOUVEMENT
    { x: -0.79, y: 0.26 },  // ENERGIE
  ];
  const PILLAR_BADGE_OFFSETS_Y = [0, 0, 0, 2.65, 2.65, 2.65]; // bottom row +10px

  SCORING_CATEGORIES.forEach((cat) => {
    const idx = PILLAR_ORDER.indexOf(cat.id);
    if (idx < 0) return;
    const pos = PILLAR_POSITIONS[idx];
    const isTopRow = idx < 3; // SOMMEIL / DIGESTION / STRESS

    const raw = computeCategoryScore(cat, responses); // /20
    const score100 = Math.round((raw / 20) * 100);
    const interp = interpretPillarRaw(raw);

    // Offsets per row
    const barDeltaY = isTopRow ? 2.65 : 3.97 + 1.32;   // bottom row +5px more
    const scoreDeltaY = isTopRow ? 3.97 + 2.65 : 5.29 + 3.97; // top +10px, bottom +15px more
    const badgeDeltaY = 6.61 + 2.65 + PILLAR_BADGE_OFFSETS_Y[idx];
    const badgeDeltaX = 2.65;
    const pillarOff = PILLAR_SCORE_OFFSETS[idx];

    // Progress bar
    const barX = pos.x - 26;
    const barY = pos.y - 14 + 15.88 + barDeltaY;
    const barW = 52;
    const barH = 3;
    doc.setFillColor(230, 226, 215);
    doc.roundedRect(barX, barY, barW, barH, 1.2, 1.2, "F");
    const fillW = Math.max(2, (score100 / 100) * barW);
    doc.setFillColor(...interp.color);
    doc.roundedRect(barX, barY, fillW, barH, 1.2, 1.2, "F");

    // Score number
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.setTextColor(56, 76, 47);
    doc.text(`${raw}`, pos.x - 26 + 1.32 + pillarOff.x, pos.y + 2 + 7.94 + scoreDeltaY + pillarOff.y);

    // Badge
    const badgeW = 24;
    const badgeH = 6.5;
    const badgeX = pos.x + 26 - badgeW + badgeDeltaX;
    const badgeY = pos.y - 3.5 + 7.94 + badgeDeltaY;
    doc.setFillColor(...interp.color);
    doc.roundedRect(badgeX, badgeY, badgeW, badgeH, 3, 3, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7);
    doc.setTextColor(255, 255, 255);
    doc.text(interp.label, badgeX + badgeW / 2, badgeY + 4.5, { align: "center" });
  });

  // === LECTURE DE VOTRE BILAN (AI text) ===
  // +30px X (+7.94mm), +15px Y (+3.97mm), font -0.5, fit all text in green box
  if (bilanText && bilanText.trim()) {
    const boxX = 18 + 7.94 + 7.94 + 2.65;
    const boxY = 246 + 3.97 + 5.29;
    const boxW = pageWidth - 36 - 30 - 7.94;
    // Available vertical space inside the green box (until ~285mm footer area)
    const boxMaxY = 286;
    let fontSize = 16.0;
    let lineH = fontSize * 0.5;
    doc.setFont("helvetica", "normal");
    doc.setTextColor(60, 60, 60);

    // Auto-shrink to fit all text inside the available area
    let lines: string[] = [];
    while (fontSize >= 5.5) {
      doc.setFontSize(fontSize);
      lines = doc.splitTextToSize(bilanText.trim(), boxW);
      lineH = fontSize * 0.5;
      const totalH = lines.length * lineH;
      if (boxY + totalH <= boxMaxY) break;
      fontSize -= 0.25;
    }
    doc.setFontSize(fontSize);
    lines.forEach((line: string, i: number) => {
      doc.text(line, boxX, boxY + i * lineH);
    });
  }

  // === Patient/date footer band (small, discreet, above the page chrome) ===
  const assessmentDate = new Date(
    assessment.assessment_date || assessment.created_at || Date.now(),
  ).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" });
  doc.setFont("helvetica", "italic");
  doc.setFontSize(7.5);
  doc.setTextColor(120, 120, 120);
  doc.text(`Consultant : ${patientName}  •  Bilan du ${assessmentDate}`, pageWidth / 2, 292, {
    align: "center",
  });

  const safeName = (patientName || "consultant").replace(/[^a-z0-9]/gi, "_");
  doc.save(`bilan_vitalite_${safeName}_${assessmentDate.replace(/\s/g, "_")}.pdf`);
}
